import React, { useCallback, useMemo, useRef, useState } from 'react';

import { useDropzone } from 'react-dropzone';

import useStyles from '../useStyles';

import { v4 } from 'uuid';
import { useSelector } from '@store';

import {
  CapaQuotationChatRoomStatus,
  CapaQuotationPartnerStatus,
  useFetchWithdrawnQuotationPartnerQuery
} from '@generated/graphql';

interface Props {
  children: React.ReactNode;
}

export interface ExtendedFile {
  fileId: string;
  originalFile: File;
}

export const DropzoneContext = React.createContext<{
  files: ExtendedFile[];
  onClearUploadFiles: (_fileId?: string) => void;
}>({ files: [], onClearUploadFiles: () => null });

const DropzoneProvider = React.forwardRef<HTMLInputElement, Props>(
  ({ children }, ref) => {
    const divRef = useRef<HTMLDivElement>(null);
    const classes = useStyles();

    const roomStatus = useSelector((state) => state.chat.chatRoomStatus);
    const partnerId = useSelector((state) => state.chat.quotationPartnerId);

    const { data } = useFetchWithdrawnQuotationPartnerQuery({
      variables: {
        quotationPartnerId: partnerId ?? ''
      },
      fetchPolicy: 'cache-first',
      skip: !partnerId
    });

    const isWithdraw =
      data?.fetchWithdrawnQuotationPartner.data?.status ===
      CapaQuotationPartnerStatus.Deleted;

    const isDisabled = roomStatus
      ? [
          CapaQuotationChatRoomStatus.ClosedClient,
          CapaQuotationChatRoomStatus.ClosedPartner,
          CapaQuotationChatRoomStatus.ClosedConfirm,
          CapaQuotationChatRoomStatus.Stopped
        ].includes(roomStatus) || isWithdraw
      : false;

    const [files, setFiles] = useState<ExtendedFile[]>([]);

    const onClearUploadFiles = useRef(() => setFiles([]));

    const onDrop = useCallback((acceptedFiles: File[]) => {
      if (acceptedFiles && acceptedFiles.length === 0) return;

      setFiles((prevFiles) => [
        ...prevFiles,
        ...acceptedFiles.map((f) => ({
          fileId: v4(),
          originalFile: f
        }))
      ]);
    }, []);

    const { getRootProps, getInputProps } = useDropzone({
      onDrop,
      noClick: true,
      disabled: isDisabled
    });

    const memoizedValue = useMemo(
      () => ({
        files: files,
        onClearUploadFiles: onClearUploadFiles.current
      }),
      [files]
    );

    return (
      <div
        ref={divRef}
        {...getRootProps({
          className: classes['Chat-Dropzone-Provider']
        })}
      >
        <input {...getInputProps()} ref={ref} />
        <DropzoneContext.Provider value={memoizedValue}>
          {children}
        </DropzoneContext.Provider>
      </div>
    );
  }
);

export default DropzoneProvider;
